import { useRef, useState } from 'react';
import { Upload, Trash2, FileText, CheckCircle, Star, AlertTriangle } from 'lucide-react';
import {
  useDocumentExamples,
  useAddDocumentExample,
  useDeleteDocumentExample,
  useSetPrimaryDocumentExample,
} from '../hooks/useDemandLetters';
import { Button, Card, ConfirmModal, IconButton, Skeleton, Tag } from './ui';
import { extractAxiosDetail } from '../lib/api';

const DOC_TYPE = 'demand_letter';

export default function ExampleLettersManager() {
  const { data, isLoading } = useDocumentExamples(DOC_TYPE);
  const addMutation = useAddDocumentExample(DOC_TYPE);
  const deleteMutation = useDeleteDocumentExample(DOC_TYPE);
  const setPrimaryMutation = useSetPrimaryDocumentExample(DOC_TYPE);
  const inputRef = useRef<HTMLInputElement>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const examples = data?.examples ?? [];
  const hasPdfOnly = examples.length > 0 && examples.every((e) => e.file_kind === 'pdf');

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await addMutation.mutateAsync(file);
    } catch {
      /* surfaced via addMutation.error */
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    await deleteMutation.mutateAsync(deleteId);
    setDeleteId(null);
  };

  return (
    <Card padding="spacious">
      <h2 className="t-card text-green-900 mb-1">Example Demand Letters</h2>
      <p className="t-caption mb-4">
        Upload one or more of your own demand letters. New letters are written in the same voice
        and style learned from all of your examples, while the structure and boilerplate follow the
        one you mark as <span className="font-medium text-ink-soft">Primary</span>. For the best
        match, upload Word (.docx) files — generated letters reuse your firm's logo, font, size, and
        margins straight from them. A PDF or scan still guides the wording but can't carry the logo
        or exact formatting.
      </p>

      {hasPdfOnly && (
        <div className="flex items-start gap-2 text-warn bg-warn-soft rounded-control px-3 py-2 mb-4 text-xs">
          <AlertTriangle size={16} className="flex-shrink-0 mt-(--space-hair)" />
          <span>
            Formatting limited — your examples are PDF only. Upload a Word (.docx) original for full
            fidelity (logo, fonts, and exact layout in generated letters).
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2 mb-4">
          <Skeleton variant="rect" className="h-9 rounded-control" />
          <Skeleton variant="rect" className="h-9 rounded-control" />
        </div>
      ) : examples.length > 0 ? (
        <ul className="space-y-2 mb-4">
          {examples.map((ex) => (
            <li
              key={ex.id}
              className="flex items-center gap-3 border border-border rounded-control px-3 py-2"
            >
              <FileText size={16} className="flex-shrink-0 text-ink-faint" />
              <span className="truncate flex-1 text-sm text-ink-soft">
                {ex.filename || 'Example letter'}
              </span>
              <Tag size="sm" tone={ex.file_kind === 'docx' ? 'good' : 'warn'} className="uppercase">
                {ex.file_kind || 'file'}
              </Tag>
              {ex.is_primary ? (
                <span className="flex items-center gap-1 text-xs text-good font-medium">
                  <Star size={12} className="fill-green-600 text-green-600" /> Primary
                </span>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Star}
                  onClick={() => setPrimaryMutation.mutate(ex.id)}
                  disabled={setPrimaryMutation.isPending}
                  title="Use this letter's structure as the primary template"
                  className="px-1 font-normal text-ink-faint hover:text-green-700 hover:bg-transparent"
                >
                  Make primary
                </Button>
              )}
              <IconButton
                icon={Trash2}
                aria-label="Remove example letter"
                title="Remove example letter"
                variant="danger-ghost"
                size="sm"
                onClick={() => setDeleteId(ex.id)}
                disabled={deleteMutation.isPending}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-ink-faint text-sm mb-4">
          No example letters yet. Upload one to teach Shale your voice and format.
        </p>
      )}

      <Button
        variant="ghost"
        size="lg"
        icon={Upload}
        onClick={() => inputRef.current?.click()}
        disabled={addMutation.isPending}
        className="border-2 border-dashed border-border font-normal text-ink-faint hover:border-green-600 hover:text-green-700 hover:bg-green-50"
      >
        {addMutation.isPending ? 'Uploading…' : 'Add Example Letter (.docx or .pdf)'}
      </Button>
      {/* eslint-disable-next-line no-restricted-syntax -- hidden file input; no ui primitive exists for file pickers */}
      <input
        ref={inputRef}
        type="file"
        accept=".docx,.pdf"
        className="hidden"
        onChange={handleUpload}
      />

      {addMutation.isError && (
        <p className="text-danger text-xs mt-2">
          {extractAxiosDetail(addMutation.error) ||
            'Upload failed. Please ensure the file is a valid .docx or .pdf under 5 MB.'}
        </p>
      )}
      {addMutation.isSuccess && (
        <p className="text-good text-xs mt-2 flex items-center gap-1">
          <CheckCircle size={12} /> Example saved — new demand letters will match your
          style.
        </p>
      )}

      <ConfirmModal
        open={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Remove Example Letter"
        message="This example letter will no longer guide the voice or structure of generated demand letters."
        confirmLabel="Remove"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </Card>
  );
}
